import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Dialog,
  DialogContent,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import {
  Button,
  Dropdown,
  Iconography,
  IconographySizes,
  Loader,
  LoaderSizes,
  Toggle,
  ToggleSizes,
} from "@hid-galaxy-ui/galaxy-react";
import type { StylesConfig } from "react-select";
import { useAppDispatch, useAppSelector } from "../../../../../../redux";
import { getPhasesSagaAction } from "../../../../../../redux/saga/technology-settings/phases/saga-actions";
import { getIntegrationByIdSagaAction } from "../../../../../../redux/saga/technology-settings/technologies/saga-actions";
import { technologySettingsTechnologiesSliceAction } from "../../../../../../redux/slices/technology-settings/technologies";
import { mapPhasesToIntegrationPhaseMappings } from "../../../../../../redux/slices/technology-settings/technologies/integrationMapper";
import type { IntegrationPhaseMapping } from "../types";
import "../../validation-management/Modals.scss";
import DescriptionEditorField from "../components/DescriptionEditorField";
import ViewDescriptionModal from "../components/ViewDescriptionModal";

export type IntegrationFormData = {
  name: string;
  description: string;
  status: boolean;
  phaseMappings: IntegrationPhaseMapping[];
};

type Props = {
  open: boolean;
  mode: "add" | "edit";
  technologyName: string;
  integrationId?: string | null;
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (data: IntegrationFormData) => void;
};

type IntegrationNameOption = { label: string; value: string };

const ENTER_NEW_INTEGRATION_NAME = "Enter New Integration Name";
const INTEGRATION_MODAL_Z_INDEX = 1400;

const integrationNameDropdownStyles: StylesConfig<IntegrationNameOption, false> =
  {
    menuPortal: (base) => ({
      ...base,
      zIndex: INTEGRATION_MODAL_Z_INDEX,
    }),
    menu: (base) => ({
      ...base,
      zIndex: INTEGRATION_MODAL_Z_INDEX + 1,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? "#e6e6e6" : base.backgroundColor,
      color: base.color,
      ":active": {
        backgroundColor: "#e6e6e6",
      },
    }),
  };

const dropdownMenuPortalTarget =
  typeof document !== "undefined" ? document.body : undefined;

const clonePhaseMappings = (phaseMappings: IntegrationPhaseMapping[]) =>
  phaseMappings.map((phase) => ({
    ...phase,
    activities: phase.activities.map((activity) => ({ ...activity })),
  }));

const resolveSelectedIntegrationOption = (
  name: string,
  existingOptions: IntegrationNameOption[]
): IntegrationNameOption => {
  const trimmedName = name.trim();
  const matchedExisting = existingOptions.find(
    (option) => option.value.toLowerCase() === trimmedName.toLowerCase()
  );

  if (matchedExisting) {
    return matchedExisting;
  }

  return {
    label: ENTER_NEW_INTEGRATION_NAME,
    value: ENTER_NEW_INTEGRATION_NAME,
  };
};

const AddIntegrationModal: React.FC<Props> = ({
  open,
  mode,
  technologyName,
  integrationId,
  isSubmitting = false,
  onClose,
  onSubmit,
}) => {
  const dispatch = useAppDispatch();
  const phases = useAppSelector((state) => state.technology_settings_phases.phases);
  const isPhasesLoading = useAppSelector(
    (state) => state.technology_settings_phases.status.phasesLoading
  );
  const technologies = useAppSelector(
    (state) => state.technology_settings_technologies.technologies
  );
  const integrationDetail = useAppSelector(
    (state) => state.technology_settings_technologies.integrationDetail
  );
  const isIntegrationDetailLoading = useAppSelector(
    (state) => state.technology_settings_technologies.status.integrationDetailLoading
  );
  const [activeTab, setActiveTab] = useState(0);
  const [expandedPhases, setExpandedPhases] = useState<string[]>([]);
  const [descriptionView, setDescriptionView] = useState<{
    title: string;
    content: string;
  } | null>(null);
  const [formData, setFormData] = useState<IntegrationFormData>({
    name: "",
    description: "",
    status: true,
    phaseMappings: [],
  });
  const [selectedIntegrationOption, setSelectedIntegrationOption] =
    useState<IntegrationNameOption | null>(null);
  const [draggedPhaseIndex, setDraggedPhaseIndex] = useState<number | null>(null);

  const isPhaseMappingsLoading =
    mode === "edit" ? isIntegrationDetailLoading : isPhasesLoading;

  const existingIntegrationOptions = useMemo(() => {
    const uniqueNames = new Map<string, string>();

    technologies.forEach((technology) => {
      technology.integrations.forEach((integration) => {
        const name = integration.name.trim();
        if (!name) return;
        const key = name.toLowerCase();
        if (!uniqueNames.has(key)) {
          uniqueNames.set(key, name);
        }
      });
    });

    return Array.from(uniqueNames.values())
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ label: name, value: name }));
  }, [technologies]);

  const integrationNameDropdownOptions = useMemo(
    () => [
      {
        label: "New Integration",
        value: "New Integration",
        options: [
          {
            label: ENTER_NEW_INTEGRATION_NAME,
            value: ENTER_NEW_INTEGRATION_NAME,
          },
        ],
      },
      {
        label: "Existing Integrations",
        value: "Existing Integrations",
        options: [...existingIntegrationOptions],
      },
    ],
    [existingIntegrationOptions]
  );

  const isEnterNewIntegrationName =
    selectedIntegrationOption?.value === ENTER_NEW_INTEGRATION_NAME;

  useEffect(() => {
    if (!open) return;

    setActiveTab(0);
    setExpandedPhases([]);
    setDescriptionView(null);
    setDraggedPhaseIndex(null);

    if (mode === "edit" && integrationId) {
      dispatch(
        getIntegrationByIdSagaAction({
          id: integrationId,
        })
      );
      return;
    }

    if (mode === "add") {
      setSelectedIntegrationOption(null);
      dispatch(getPhasesSagaAction());
    }
  }, [open, mode, integrationId, dispatch]);

  useEffect(() => {
    if (!open || mode !== "add" || isPhasesLoading) return;

    setFormData({
      name: "",
      description: "",
      status: true,
      phaseMappings: clonePhaseMappings(
        mapPhasesToIntegrationPhaseMappings(phases, false)
      ),
    });
    setSelectedIntegrationOption(null);
  }, [open, mode, phases, isPhasesLoading]);

  useEffect(() => {
    if (!open || mode !== "edit" || !integrationDetail) return;

    setFormData({
      name: integrationDetail.name,
      description: integrationDetail.description,
      status: integrationDetail.status,
      phaseMappings: clonePhaseMappings(integrationDetail.phaseMappings),
    });
  }, [open, mode, integrationDetail]);

  useEffect(() => {
    if (!open || mode !== "edit" || !integrationDetail) return;

    setSelectedIntegrationOption(
      resolveSelectedIntegrationOption(
        integrationDetail.name,
        existingIntegrationOptions
      )
    );
  }, [open, mode, integrationDetail, existingIntegrationOptions]);

  const handleClose = () => {
    dispatch(technologySettingsTechnologiesSliceAction.setIntegrationDetail(null));
    onClose();
  };

  const onIntegrationNameSelect = (data: IntegrationNameOption | null) => {
    setSelectedIntegrationOption(data);

    if (data?.value === ENTER_NEW_INTEGRATION_NAME) {
      setFormData((prev) => ({
        ...prev,
        name: mode === "edit" ? prev.name : "",
      }));
      return;
    }

    if (data?.value) {
      setFormData((prev) => ({ ...prev, name: data.value }));
      return;
    }

    setFormData((prev) => ({ ...prev, name: "" }));
  };

  const isFormValid = useMemo(
    () => Boolean(formData.name.trim()),
    [formData.name]
  );

  const handleSubmit = () => {
    if (!isFormValid || isSubmitting || isPhaseMappingsLoading) return;
    onSubmit(formData);
  };

  const togglePhaseExpanded = (phaseId: string) => {
    setExpandedPhases((prev) =>
      prev.includes(phaseId)
        ? prev.filter((id) => id !== phaseId)
        : [...prev, phaseId]
    );
  };

  const toggleActivityMapped = (phaseId: string, activityId: string) => {
    setFormData((prev) => ({
      ...prev,
      phaseMappings: prev.phaseMappings.map((phase) =>
        phase.id !== phaseId
          ? phase
          : {
              ...phase,
              activities: phase.activities.map((activity) =>
                activity.id === activityId
                  ? { ...activity, mapped: !activity.mapped }
                  : activity
              ),
            }
      ),
    }));
  };

  const handlePhaseDragStart = (
    event: React.DragEvent<HTMLSpanElement>,
    index: number
  ) => {
    setDraggedPhaseIndex(index);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(index));
  };

  const handlePhaseDrop = (
    event: React.DragEvent<HTMLDivElement>,
    targetIndex: number
  ) => {
    event.preventDefault();
    const sourceFromDataTransfer = Number(event.dataTransfer.getData("text/plain"));
    const sourceIndex =
      draggedPhaseIndex !== null
        ? draggedPhaseIndex
        : Number.isNaN(sourceFromDataTransfer)
          ? null
          : sourceFromDataTransfer;

    if (sourceIndex === null || sourceIndex === targetIndex) {
      setDraggedPhaseIndex(null);
      return;
    }

    setFormData((prev) => {
      const mappings = [...prev.phaseMappings];
      if (
        sourceIndex < 0 ||
        targetIndex < 0 ||
        sourceIndex >= mappings.length ||
        targetIndex >= mappings.length
      ) {
        return prev;
      }
      const [movedPhase] = mappings.splice(sourceIndex, 1);
      mappings.splice(targetIndex, 0, movedPhase);
      return {
        ...prev,
        phaseMappings: mappings.map((phase, index) => ({
          ...phase,
          order: index + 1,
        })),
      };
    });
    setDraggedPhaseIndex(null);
  };

  const phaseMappingSummary = useMemo(
    () =>
      formData.phaseMappings.map((phase) => {
        const mappedCount = phase.activities.filter((a) => a.mapped).length;
        return {
          ...phase,
          mappedCount,
          totalCount: phase.activities.length,
        };
      }),
    [formData.phaseMappings]
  );

  const subtitle =
    mode === "add"
      ? "Configure a new integration"
      : `Configure a new integration for ${technologyName}`;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        className="requirement-modal technology-management-modal tm-integration-dialog"
      >
        <div className="benefits-ptv-modal tm-modal tm-integration-modal">
          <header className="benefits-ptv-modal-header">
            <div>
              <p className="benefits-ptv-modal-eyebrow">Technology Configuration</p>
              <h3>{mode === "add" ? "Add New Integration" : "Edit Integration"}</h3>
              <p className="benefits-ptv-modal-subtitle">{subtitle}</p>
            </div>
            <button
              type="button"
              className="benefits-ptv-modal-close"
              onClick={handleClose}
              aria-label="Close"
            >
              <Iconography icon="xmark" size={IconographySizes.Medium} />
            </button>
          </header>

          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            className="modal-tabs tm-integration-modal-tabs"
          >
            <Tab label="Integration Details" />
            <Tab label="Integration Phases & Activities" />
          </Tabs>

          <DialogContent className="benefits-ptv-modal-content tm-integration-modal-content">
            {activeTab === 0 && (
              <Box>
                <div className="tm-form-row">
                  <div className="form-field tm-integration-name-field">
                    <label htmlFor="integration-name">Integration Name*</label>
                    <Dropdown
                      id="integration-name"
                      label=""
                      placeholder="Select"
                      theme="dark"
                      options={integrationNameDropdownOptions}
                      selected={selectedIntegrationOption}
                      isSearchable={true}
                      menuPortalTarget={dropdownMenuPortalTarget}
                      styles={integrationNameDropdownStyles}
                      onSelect={onIntegrationNameSelect}
                    />
                  </div>
                  <div className="form-field tm-toggle-field">
                    <label htmlFor="integration-status">Integration Status*</label>
                    <div className="tm-toggle-row">
                      <Toggle
                        id="integration-status"
                        labelText=""
                        labelON=""
                        labelOFF=""
                        toggled={formData.status}
                        onClick={() =>
                          setFormData((prev) => ({ ...prev, status: !prev.status }))
                        }
                        size={ToggleSizes.Mini}
                      />
                      <span>{formData.status ? "Active" : "Inactive"}</span>
                    </div>
                  </div>
                </div>

                {selectedIntegrationOption ? (
                  <div className="form-field tm-integration-name-input">
                    <label htmlFor="integration-name-input">
                      {isEnterNewIntegrationName
                        ? "Enter New Integration Name*"
                        : "Integration Name*"}
                    </label>
                    <TextField
                      id="integration-name-input"
                      fullWidth
                      variant="outlined"
                      placeholder="e.g., Technical Excellence"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                    />
                  </div>
                ) : null}

                <DescriptionEditorField
                  id="integration-description"
                  placeholder="Enter integration description"
                  value={formData.description}
                  onChange={(description) =>
                    setFormData((prev) => ({ ...prev, description }))
                  }
                />
              </Box>
            )}

            {activeTab === 1 && (
              <Box className="tm-phase-mapping-list">
                {isPhaseMappingsLoading ? (
                  <div className="feature-loader-wrap">
                    <Loader variant={LoaderSizes.large} />
                  </div>
                ) : phaseMappingSummary.length === 0 ? (
                  <p className="feature-empty-cell">No integration phases available.</p>
                ) : (
                  phaseMappingSummary.map((phase, index) => {
                    const isExpanded = expandedPhases.includes(phase.id);
                    const isDragging = draggedPhaseIndex === index;
                    return (
                      <div
                        key={phase.id}
                        className={`tm-phase-mapping-item${
                          isDragging ? " tm-row-dragging" : ""
                        }`}
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={(event) => handlePhaseDrop(event, index)}
                      >
                        <span
                          className="tm-drag-handle drag-handle tm-phase-mapping-drag"
                          draggable
                          onDragStart={(event) => handlePhaseDragStart(event, index)}
                          onDragEnd={() => setDraggedPhaseIndex(null)}
                          title="Drag to reorder"
                          aria-label="Drag to reorder"
                          role="button"
                        >
                          <span className="drag-dots-icon" aria-hidden="true" />
                        </span>
                        <div className="tm-phase-mapping-card">
                          <button
                            type="button"
                            className={`tm-phase-mapping-header${
                              isExpanded ? " tm-phase-mapping-header--expanded" : ""
                            }`}
                            onClick={() => togglePhaseExpanded(phase.id)}
                          >
                            <span className="tm-phase-badge">{phase.order}</span>
                            <span className="tm-phase-mapping-title">{phase.name}</span>
                            <span className="tm-phase-mapping-count">
                              {phase.mappedCount}/{phase.totalCount} Mapped
                            </span>
                            <Iconography
                              icon={isExpanded ? "angleUp" : "angleDown"}
                              size={IconographySizes.Small}
                              className="tm-phase-mapping-chevron"
                            />
                          </button>
                          {isExpanded && (
                            <div className="tm-phase-mapping-table">
                              <div className="tm-phase-mapping-table-head">
                                <span className="tm-phase-mapping-col-name">
                                  Activity Name
                                </span>
                                <span className="tm-phase-mapping-col-desc">
                                  Description
                                </span>
                                <span className="tm-phase-mapping-col-mapped">
                                  Mapped
                                </span>
                              </div>
                              {phase.activities.map((activity) => (
                                <div key={activity.id} className="tm-phase-mapping-row">
                                  <span className="tm-phase-mapping-col-name">
                                    {activity.name}
                                  </span>
                                  <span className="tm-phase-mapping-col-desc">
                                    <button
                                      type="button"
                                      className="view-link"
                                      onClick={() =>
                                        setDescriptionView({
                                          title: activity.name,
                                          content: activity.description || "",
                                        })
                                      }
                                    >
                                      View
                                    </button>
                                  </span>
                                  <span className="tm-phase-mapping-col-mapped">
                                    <Checkbox
                                      checked={activity.mapped}
                                      onChange={() =>
                                        toggleActivityMapped(phase.id, activity.id)
                                      }
                                      size="small"
                                      className="tm-phase-mapping-checkbox"
                                    />
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </Box>
            )}
          </DialogContent>

          <footer className="benefits-ptv-modal-footer">
            <Button variant="secondary" label="CANCEL" onClick={handleClose} />
            <Button
              variant="primary"
              label={mode === "add" ? "ADD INTEGRATION" : "SAVE INTEGRATION"}
              onClick={handleSubmit}
              disabled={!isFormValid || isSubmitting || isPhaseMappingsLoading}
            />
          </footer>
        </div>
      </Dialog>

      <ViewDescriptionModal
        open={Boolean(descriptionView)}
        eyebrow="Technology Configuration"
        title="View Description"
        subtitle={descriptionView?.title || ""}
        content={descriptionView?.content || ""}
        onClose={() => setDescriptionView(null)}
      />
    </>
  );
};

export default AddIntegrationModal;
